import { openDB, IDBPDatabase } from 'idb'

const MAX_ENTRIES = 50000 // Store only the latest 50,000 addresses
const EXPIRY_DAYS = 30 // Delete entries older than 30 days

// Round lat/lng to 4 decimal places for deduplication
const roundCoordinate = (num: number, decimals = 4): number =>
    parseFloat(num.toFixed(decimals))

const openGeoDB = async () => {
    return openDB('GeoCacheDB', 1, {
        upgrade(db) {
            if (!db.objectStoreNames.contains('geoCache')) {
                const store = db.createObjectStore('geoCache', {
                    keyPath: 'latLng',
                })
                store.createIndex('timestamp', 'timestamp')
            }
        },
    })
}

// Store address in IndexedDB
const storeAddress = async (
    lat: number,
    lng: number,
    address: string,
): Promise<void> => {
    const db = await openGeoDB()
    const timestamp = Date.now()
    const roundedLatLng = `${roundCoordinate(lat)},${roundCoordinate(lng)}`

    await db.put('geoCache', { latLng: roundedLatLng, address, timestamp })

    await cleanOldEntries(db)
}

// Fetch address from IndexedDB
const getCachedAddress = async (lat: number, lng: number) => {
    const db = await openGeoDB()
    const roundedLatLng = `${roundCoordinate(lat)},${roundCoordinate(lng)}`
    return db.get('geoCache', roundedLatLng)
}

// Clean up IndexedDB to prevent performance issues

const cleanOldEntries = async (db: IDBPDatabase): Promise<void> => {
    const tx = db.transaction('geoCache', 'readwrite')
    const store = tx.objectStore('geoCache')

    // Get all keys
    const keys = await store.getAllKeys()
    if (keys.length > MAX_ENTRIES) {
        const oldEntries = await store.index('timestamp').getAll()
        oldEntries.sort(
            (a: { timestamp: number }, b: { timestamp: number }) =>
                a.timestamp - b.timestamp,
        ) // Oldest first

        // Delete excess entries
        for (let i = 0; i < keys.length - MAX_ENTRIES; i++) {
            await store.delete(oldEntries[i].latLng)
        }
    }

    // Remove entries older than EXPIRY_DAYS
    const expiryTime = Date.now() - EXPIRY_DAYS * 24 * 60 * 60 * 1000
    const allEntries = await store.getAll()
    for (const entry of allEntries) {
        if (entry.timestamp < expiryTime) {
            await store.delete(entry.latLng)
        }
    }
}

// Call Google Maps API for Reverse Geocoding
const fetchAddressFromGoogle = async (
    lat: number,
    lng: number,
    apiKey: string,
): Promise<string | null> => {
    const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`,
    )
    const data = await response.json()
    return data.results[0]?.formatted_address || null
}

// Get Address with Caching Mechanism
export const getAddress = async (
    lat: number,
    lng: number,
    apiKey: string,
): Promise<string | null> => {
    const cachedAddress = await getCachedAddress(lat, lng)
    if (cachedAddress) {
        return cachedAddress.address
    }

    // If not cached, fetch from Google
    const address = await fetchAddressFromGoogle(lat, lng, apiKey)
    if (address) await storeAddress(lat, lng, address)

    return address
}
